import React from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaMicrochip, FaCogs, FaDatabase, FaReact, FaPython, FaAws, FaDocker } from 'react-icons/fa';
import { SiTensorflow, SiMongodb } from 'react-icons/si';
import { fadeInUp, staggerContainer, staggerItem } from '../../utils/animations';
import PageLayout from '../../Components/layout/PageLayout';
import Section from '../../Components/common/Section';
import ServiceCard from '../../Components/common/ServiceCard';
import GradientText from '../../Components/common/GradientText';
import CTASection from '../../Components/common/CTASection';

const Services = () => {
  const services = [
    {
      icon: FaCode,
      title: 'Custom Web Applications',
      description: 'Full-stack development with React, Node.js, and cloud deployment. Scalable, responsive, and production-ready applications.',
      link: '/contact',
    },
    {
      icon: FaMicrochip,
      title: 'AI/ML Integrations',
      description: 'Integrate ChatGPT, computer vision, NLP, and custom AI models into your existing systems.',
      link: '/contact',
    },
    {
      icon: FaCogs,
      title: 'Business Automation Tools',
      description: 'Automate workflows, data pipelines, and repetitive tasks. API integrations and RPA solutions.',
      link: '/contact',
    },
    {
      icon: FaDatabase,
      title: 'SaaS & Dashboards',
      description: 'Multi-tenant SaaS platforms, real-time analytics dashboards, and admin panels.',
      link: '/contact',
    },
  ];

  const technologies = [
    { icon: FaReact, name: 'React' },
    { icon: FaPython, name: 'Python' },
    { icon: SiTensorflow, name: 'TensorFlow' },
    { icon: FaAws, name: 'AWS' },
    { icon: SiMongodb, name: 'MongoDB' },
    { icon: FaDocker, name: 'Docker' },
  ];

  const process = [
    { step: '1', title: 'Discovery', description: 'Understand your requirements and goals' },
    { step: '2', title: 'Design', description: 'Create wireframes and technical architecture' },
    { step: '3', title: 'Development', description: 'Build with agile methodology and regular updates' },
    { step: '4', title: 'Testing', description: 'Comprehensive QA and user acceptance testing' },
    { step: '5', title: 'Deployment', description: 'Launch and monitor with ongoing support' },
  ];

  return (
    <PageLayout>
      {/* Hero */}
      <Section background="dark" className="py-20">
        <motion.div {...fadeInUp} className="text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Custom <GradientText>AI Solutions</GradientText> for Your Business
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            From idea to deployment - we build scalable web applications, integrate AI capabilities, and automate your workflows.
          </p>
        </motion.div>
      </Section>

      {/* Services */}
      <Section background="white">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div variants={staggerItem} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              What We <GradientText>Build</GradientText>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div key={index} variants={staggerItem}>
                <ServiceCard {...service} />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* Tech Stack */}
      <Section background="light">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={staggerItem} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Technologies We <GradientText>Use</GradientText>
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-8">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="flex flex-col items-center"
              >
                <div className="w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center mb-3 hover:scale-110 transition-transform">
                  <tech.icon className="text-4xl text-primary-blue" />
                </div>
                <p className="text-gray-700 font-semibold">{tech.name}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* Process */}
      <Section background="white">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div variants={staggerItem} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Our <GradientText>Process</GradientText>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {process.map((item, index) => (
              <motion.div key={index} variants={staggerItem} className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-primary text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* CTA */}
      <CTASection
        title="Have a Project in Mind?"
        description="Let's discuss how we can bring your ideas to life with custom AI-powered solutions"
        buttonText="Get a Quote"
        buttonLink="/contact"
      />
    </PageLayout>
  );
};

export default Services;
